import { useEffect, useState } from 'react'
import './uiTheme.css'

interface LandingPageProps {
  onOpenLogin: () => void
  onOpenSignUp: () => void
}

type CookiePreference = 'accepted' | 'rejected'

const COOKIE_PREFERENCE_KEY = 'alberto:cookie-preference'

const HERO_CHAT_PROMPTS = [
  'Ask about your health...',
  'Summarize my latest blood work...',
  'Show trends in my sleep and HRV...',
  'What should I improve this week?',
  'Help me prepare for my next doctor visit...',
]

const HERO_CHAT_PROMPT_INTERVAL_MS = 5000
const HERO_CHAT_PROMPT_FADE_MS = 240

function loadCookiePreference(): CookiePreference | null {
  const raw = window.localStorage.getItem(COOKIE_PREFERENCE_KEY)
  if (raw === 'accepted' || raw === 'rejected') {
    return raw
  }

  return null
}

function HeroSparkIcon({ className }: { className?: string }) {
  return (
    <svg
      aria-hidden="true"
      className={className}
      focusable="false"
      viewBox="0 0 100 100"
    >
      <path
        d="M50 0C56 18 69 31 100 50C69 69 56 82 50 100C44 82 31 69 0 50C31 31 44 18 50 0Z"
        fill="currentColor"
      />
    </svg>
  )
}


function HeroChatBox() {
  const [promptIndex, setPromptIndex] = useState(0)
  const [isPromptVisible, setIsPromptVisible] = useState(true)
  const [chatValue, setChatValue] = useState('')
  const [isInputFocused, setIsInputFocused] = useState(false)

  useEffect(() => {
    let fadeTimeoutId: number | undefined
    const intervalId = window.setInterval(() => {
      setIsPromptVisible(false)
      fadeTimeoutId = window.setTimeout(() => {
        setPromptIndex((currentIndex) => (currentIndex + 1) % HERO_CHAT_PROMPTS.length)
        setIsPromptVisible(true)
      }, HERO_CHAT_PROMPT_FADE_MS)
    }, HERO_CHAT_PROMPT_INTERVAL_MS)

    return () => {
      window.clearInterval(intervalId)
      if (fadeTimeoutId !== undefined) {
        window.clearTimeout(fadeTimeoutId)
      }
    }
  }, [])

  const shouldShowPrompt = chatValue.length === 0 && !isInputFocused

  return (
    <div className="ui-hero-chat-wrap">
      <div className="ui-hero-chat">
        <div className="ui-hero-chat-input-shell">
          <textarea
            className="ui-hero-chat-input"
            value={chatValue}
            onChange={(event) => setChatValue(event.target.value)}
            onFocus={() => setIsInputFocused(true)}
            onBlur={() => setIsInputFocused(false)}
            aria-label="Ask about your health"
            rows={3}
          />
          <div className={`ui-hero-chat-placeholder ${shouldShowPrompt && isPromptVisible ? 'is-visible' : 'is-hidden'}`}>
            {HERO_CHAT_PROMPTS[promptIndex]}
          </div>
        </div>
        <div className="ui-hero-chat-toolbar">
          <span className="ui-hero-chat-send">
            <span className="ui-hero-chat-send-icon" aria-hidden="true">
              &#8593;
            </span>
          </span>
        </div>
      </div>
      <div className="ui-hero-social-proof" aria-label="Trust indicators">
        <p className="ui-hero-trusted-by">Trusted by 200,000+ people optimizing their health</p>
        <ul className="ui-hero-trust-metrics" aria-label="Platform metrics">
          <li>
            <strong>4.9/5</strong>
            <span>average member rating</span>
          </li>
          <li>
            <strong>50+</strong>
            <span>device integrations</span>
          </li>
          <li>
            <strong>24/7</strong>
            <span>chat and email support</span>
          </li>
        </ul>
      </div>
    </div>
  )
}


export default function LandingPage({ onOpenLogin, onOpenSignUp }: LandingPageProps) {
  const [cookiePreference, setCookiePreference] = useState<CookiePreference | null>(loadCookiePreference)
  const [isHeroHeader, setIsHeroHeader] = useState(true)

  useEffect(() => {
    document.documentElement.classList.add('unified-scroll')
    document.body.classList.add('unified-scroll')

    const syncHeaderMode = () => {
      const scrollY = window.scrollY
      const viewport = window.innerHeight
      const isMobileViewport = window.innerWidth <= 1250
      const heroCutoff = isMobileViewport ? viewport * 4.2 : viewport * 0.64
      setIsHeroHeader(scrollY < heroCutoff)
    }

    syncHeaderMode()
    window.addEventListener('scroll', syncHeaderMode, { passive: true })

    return () => {
      document.documentElement.classList.remove('unified-scroll')
      document.body.classList.remove('unified-scroll')
      window.removeEventListener('scroll', syncHeaderMode)
    }
  }, [])

  function saveCookiePreference(nextPreference: CookiePreference) {
    window.localStorage.setItem(COOKIE_PREFERENCE_KEY, nextPreference)
    setCookiePreference(nextPreference)
  }

  return (
    <div className="unified-shell landing-theme landing-home-theme">
      <div className="ui-bloom-bg" aria-hidden="true">
        <div className="ui-bloom-blob ui-bloom-blob-1" />
        <div className="ui-bloom-blob ui-bloom-blob-2" />
        <div className="ui-bloom-blob ui-bloom-blob-3" />
      </div>

      <header className={`ui-topbar ${isHeroHeader ? '' : 'ui-topbar-scrolled'}`}>
        <a href="#hero" className="ui-logo" aria-label="Alberto Health home">
          <span className="ui-logo-wordmark">Alberto Health</span>
        </a>


        <div className="ui-topbar-actions">
          <button type="button" className="ui-pill-button ui-pill-button-login" onClick={onOpenLogin}>
            Log in
          </button>
          <button type="button" className="ui-pill-button ui-pill-button-signup" onClick={onOpenSignUp}>
            Sign up
          </button>
        </div>
      </header>

      <main>
        <section id="hero" className="ui-scene ui-scene-hero">
          <div className="ui-hero-layout">
            <div className="ui-hero-stack">
              <h1 className="ui-hero-headline-eb-garamond" aria-label="Your complete health, in one place">
                <span className="ui-hero-headline-row">
                  <span className="ui-hero-headline-copy">Your complete</span>
                  <span className="ui-hero-headline-highlight-wrap">
                    <HeroSparkIcon className="ui-hero-headline-spark ui-hero-headline-spark-left" />
                    <span className="ui-hero-headline-highlight">health</span>
                    <span className="ui-hero-headline-punctuation">,</span>
                    <HeroSparkIcon className="ui-hero-headline-spark ui-hero-headline-spark-right" />
                  </span>
                  <span className="ui-hero-headline-copy">in one place</span>
                </span>
              </h1>
              <p className="ui-hero-subtitle">
                Track labs, vitals, and wellness data — all analyzed by AI to keep you ahead.
              </p>
              <HeroChatBox />
            </div>
          </div>

          <footer className="ui-site-footer" aria-label="Site footer">
            <div className="ui-footer-row">
              <div className="ui-footer-left">
                <span className="ui-footer-brand-name">Alberto Health</span>
                <nav className="ui-footer-nav-links" aria-label="Footer navigation">
                  <a href="/discover" onClick={(e) => e.preventDefault()}>Discover</a>
                  <a href="/pricing" onClick={(e) => e.preventDefault()}>Pricing</a>
                  <a href="/help" onClick={(e) => e.preventDefault()}>Help</a>
                </nav>
              </div>
              <div className="ui-footer-legal-links">
                <a href="/terms" onClick={(e) => e.preventDefault()}>Terms</a>
                <a href="/privacy" onClick={(e) => e.preventDefault()}>Privacy</a>
                <a href="/security" onClick={(e) => e.preventDefault()}>Security</a>
              </div>
            </div>
          </footer>
        </section>

      </main>

      {cookiePreference === null && (
        <aside className="ui-cookie-banner" aria-label="Cookie banner">
          <button
            type="button"
            className="ui-cookie-close"
            aria-label="Close banner and reject non-essential cookies"
            onClick={() => saveCookiePreference('rejected')}
          >
            &times;
          </button>
          <p className="ui-cookie-text">
            We use cookies to improve your experience and analyze site usage. Your health data is
            never used for advertising. Learn more in our{' '}
            <a href="/privacy" onClick={(event) => event.preventDefault()}>
              Privacy Policy
            </a>
            .
          </p>
          <div className="ui-cookie-actions" aria-label="Cookie preference actions">
            <button
              type="button"
              className="ui-cookie-action ui-cookie-action-reject"
              onClick={() => saveCookiePreference('rejected')}
            >
              Reject
            </button>
            <button
              type="button"
              className="ui-cookie-action ui-cookie-action-accept"
              onClick={() => saveCookiePreference('accepted')}
            >
              Accept
            </button>
          </div>
        </aside>
      )}

      <a href="/download" className="ui-mobile-download" onClick={(event) => event.preventDefault()}>
        Download Alberto
      </a>
    </div>
  )
}
